import Cookies from 'js-cookie';
import isEmpty from 'lodash.isempty';
import { v4 as uuid } from 'uuid';

import type { APIAuthUserData } from '~/services/users/types';
import { safeJSONParse } from '~/common/utils/safeJSONParse.util';
import { isBrowserFn } from '~/utils/browser';
import {
  deleteSessionStorage,
  readSessionStorage,
  writeSessionStorage,
  updateSessionStorage,
} from '~/utils/storage';
import isWebView from '~/utils/isWebView';

import { URLQueryKeys } from '../common/types/URLQueryKeys';

import type { AnalyticsUser } from './types';

type UtmParams =
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_term'
  | 'utm_content'
  | 'utm_channel';
type ParsedUtmParams = Record<UtmParams, string>;

const ANONYMOUS_ID_KEY = 'ajs_anonymous_id';
const ANONYMOUS_ID_PREFIX = 'anonymous-user-';
const ANALYTICS_SESSION_ID = 'analytics-session-id-fallback';
const USER_DATA_KEY = 'user';
const USER_TRAITS_KEY = 'analytics-user-traits';
const FIRST_TOUCH_UTM_KEY = 'first_touch_utm';
const LAST_TOUCH_UTM_KEY = 'last_touch_utm';
const REFERRAL_CODE = 'referral_code';
const WEBVIEW_KEY = 'webView';

/** Parse <contest-id> from URL
 *
 * url schema: <environment>.splashsports.com/contest/<contest-id>/*
 */
function getContestIdFromUrl(url: string): string | undefined {
  // Regex that returns url path after "contest/", delimited by '/'
  // eslint-disable-next-line no-useless-escape
  const regex = /contest\/([^\/]*)/;
  const urlPath = url.match(regex)?.[1];

  return urlPath;
}

function parseBrandMeta(): {
  brand: string;
  brandPlatform: string;
  webView: boolean;
} {
  if (!isBrowserFn()) return;

  const urlParams = new URLSearchParams(window.location.search);

  return {
    brand: urlParams.get('brand')?.toLowerCase(),
    brandPlatform: urlParams.get('platform')?.toLowerCase(),
    webView: isWebView(),
  };
}

function parseUTMparamsFromURL(url?: string): ParsedUtmParams | undefined {
  // If not in browser (i.e. tests) and url is not provided, return undefined (to avoid errors)
  if (!isBrowserFn() && !url) return;

  // If URL not provided, use the current URL search params
  const urlParams = new URLSearchParams(url || window.location.search);
  const parsedUTMparameters = {} as ParsedUtmParams;

  // UTM parsing
  urlParams.forEach((value, key) => {
    const queryKey = key.match(/utm_([^=]*)/i)?.[0] as UtmParams | undefined;
    if (queryKey) {
      Object.assign(parsedUTMparameters, { [queryKey.toLowerCase()]: value });
      return;
    }

    // Appsflyer query -> UTM mapping (because their deeplinks use custom params...)
    switch (key) {
      case 'pid':
        parsedUTMparameters.utm_source = value;
        break;
      case 'c':
        parsedUTMparameters.utm_campaign = value;
        break;
      case 'af_adset':
        parsedUTMparameters.utm_medium = value;
        break;
      case 'af_ad':
        parsedUTMparameters.utm_content = value;
        break;
      case 'af_channel':
        parsedUTMparameters.utm_channel = value;
        break;
      case 'af_keywords':
        parsedUTMparameters.utm_term = value;
        break;
      default:
        break;
    }
  });

  return parsedUTMparameters;
}

function parseReferralCodeFromURL(url?: string): string | undefined {
  if (!isBrowserFn() && !url) return;

  const urlParams = new URLSearchParams(url || window.location.search);
  return urlParams.get(URLQueryKeys.REFERRAL_CODE);
}

const setAnonymousId = (id = `${ANONYMOUS_ID_PREFIX}${uuid()}`) => {
  Cookies.set(ANONYMOUS_ID_KEY, id, {
    expires: 365,
    domain: 'splashsports.com',
  });
  return id;
};

const getAnonymousId = () => {
  // Get the anonymous ID from the URL if it's set
  const urlParams = new URLSearchParams(window?.location?.search);
  const urlAnonymousID = urlParams.get('ref_anon_id');

  return urlAnonymousID || Cookies.get(ANONYMOUS_ID_KEY) || setAnonymousId();
};

const getStoredUser = () => readSessionStorage<APIAuthUserData>(USER_DATA_KEY);

const getUserTraits = () => readSessionStorage<Partial<AnalyticsUser>>(USER_TRAITS_KEY);
const updateUserTraits = (traits: Partial<AnalyticsUser>) =>
  updateSessionStorage(USER_TRAITS_KEY, traits);
const deleteUserTraits = () => deleteSessionStorage(USER_TRAITS_KEY);

const WEBVIEW_COOKIE_EXPIRY_DAYS = 365;

const setWebViewCookie = () =>
  isWebView() && Cookies.set(WEBVIEW_KEY, 'true', { expires: WEBVIEW_COOKIE_EXPIRY_DAYS });

// EDGE-30402: mirror first/last-touch UTM into a cookie scoped to
// `splashsports.com`, for carryover to contest V2 microfrontend.
const UTM_COOKIE_DOMAIN = 'splashsports.com';

const isNonEmpty = (params: ParsedUtmParams | undefined): params is ParsedUtmParams =>
  !isEmpty(params);

const readUtmCookie = (key: string): ParsedUtmParams | undefined => {
  const raw = Cookies.get(key);
  return raw ? safeJSONParse(raw) : undefined;
};

const writeUtmCookie = (key: string, params: ParsedUtmParams) => {
  Cookies.set(key, JSON.stringify(params), { domain: UTM_COOKIE_DOMAIN });
};

const getFirstTouchUTM = () => readSessionStorage<ParsedUtmParams>(FIRST_TOUCH_UTM_KEY);
const setFirstTouchUTM = () => {
  const params = parseUTMparamsFromURL();
  writeSessionStorage(FIRST_TOUCH_UTM_KEY, params);
  // Write-once: never overwrite a first-touch already carried this session
  // (mirrors the "first" semantics of sessionStorage's single bootstrap write).
  if (isNonEmpty(params) && !readUtmCookie(FIRST_TOUCH_UTM_KEY)) {
    writeUtmCookie(FIRST_TOUCH_UTM_KEY, params);
  }
};
const getLastTouchUTM = () => readSessionStorage<ParsedUtmParams>(LAST_TOUCH_UTM_KEY);
const setLastTouchUTM = () => {
  const params = parseUTMparamsFromURL();
  updateSessionStorage(LAST_TOUCH_UTM_KEY, params);
  // Merge into the cookie the same way updateSessionStorage merges storage, so
  // the cookie keeps the latest value of each utm_* key.
  if (isNonEmpty(params)) {
    writeUtmCookie(LAST_TOUCH_UTM_KEY, { ...readUtmCookie(LAST_TOUCH_UTM_KEY), ...params });
  }
};
const getReferralCode = () => readSessionStorage<string>(REFERRAL_CODE);
const setReferralCode = () => {
  const referralCode = parseReferralCodeFromURL();
  if (referralCode) writeSessionStorage(REFERRAL_CODE, referralCode);
};

type GA4SessionData = {
  ga_session_id: string;
  ga_session_number: string;
  gclid?: string;
};
function getGA4SessionData(): GA4SessionData | undefined {
  if (!isBrowserFn()) return;

  // GA4 stream ID that stores the cookie for splashsports.com
  const GAstreamId = process.env.NEXT_PUBLIC_GA4_STREAMID;
  const streamCookie = Cookies.get(`_ga_${GAstreamId}`);
  const conversionCookie = Cookies.get(`_gcl_aw`);

  const parsedStreamCookie = streamCookie?.split('.').slice(2, -1);
  const parsedConversionCookie = conversionCookie?.split('.').slice(2);

  // eslint-disable-next-line local-rules/require-let-annotation
  let sessionID;
  // eslint-disable-next-line local-rules/require-let-annotation
  let sessionNumber;

  if (parsedStreamCookie) {
    sessionID = parsedStreamCookie.shift();
    sessionNumber = parsedStreamCookie.shift();
  }
  else {
    // If GA session ID is not stored, get or generate the fallback
    sessionID = Cookies.get(ANALYTICS_SESSION_ID);
    sessionNumber = '1';

    if (!sessionID) {
      sessionID = Date.now().toString().slice(0, -3);
      Cookies.set(ANALYTICS_SESSION_ID, sessionID);
    }
  }

  return {
    ga_session_id: sessionID,
    ga_session_number: sessionNumber,
    gclid: parsedConversionCookie?.join('.'),
  };
}

type FBSessionData = {
  fbp: string;
  fbc: string;
};
// TODO: Parse from URL, because we won't have a cookie...
// https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
function getFBSessionData(): FBSessionData | undefined {
  if (!isBrowserFn()) return;

  // Parse URL parameter, fallback on cookie
  const fbClid = window.location.search.match(/[&?]fbclid=([^&]*)/)?.[1];
  const formattedFbc = fbClid ? `fb.1.${Date.now()}.${fbClid}` : undefined;

  const fbp = Cookies.get('_fbp');
  const fbc = formattedFbc || Cookies.get('_fbc');

  return { fbp, fbc };
}

const removeAnalyticsSessionId = () => {
  Cookies.remove(ANALYTICS_SESSION_ID);
};

export {
  // Analytics meta
  getAnonymousId,
  getFBSessionData,
  getGA4SessionData,

  // User data
  getStoredUser,
  deleteUserTraits,
  getUserTraits,
  updateUserTraits,

  // UTM params
  parseBrandMeta,
  setWebViewCookie,
  getFirstTouchUTM,
  setFirstTouchUTM,
  getLastTouchUTM,
  setLastTouchUTM,

  // Referral code
  getReferralCode,
  setReferralCode,

  // Misc
  getContestIdFromUrl,
  removeAnalyticsSessionId,

  // Constants
  ANONYMOUS_ID_KEY,
  ANONYMOUS_ID_PREFIX,
};
